import {
  PULL_TAG,
  PUSH_TAG,
} from '@/app/sync/handoff';
import { useEffect } from 'react';

// stages every unpushed change for the service worker; how many there were
export type Leave = () => Promise<number>;

// Chrome's, and missing from the DOM typings
type SyncRegistration = ServiceWorkerRegistration & {
  sync?: { register(tag: string): Promise<void> },
  periodicSync?: {
    register(tag: string, options: { minInterval: number }): Promise<void>,
  },
};

// a hint: Chrome decides, by how much the app is used
const PULL_EVERY_MS = 6 * 60 * 60 * 1000;

// none under `yarn dev`, which serves no service worker
const registration = async () =>
  'serviceWorker' in navigator
    ? await navigator.serviceWorker.getRegistration() as
      | SyncRegistration
      | undefined
    : undefined;

// only granted to an installed app
const registerPull = async () => {
  const reg = await registration();
  if (!reg?.periodicSync) return;
  const { state } = await navigator.permissions.query({
    name: 'periodic-background-sync' as PermissionName,
  });
  if (state === 'granted') {
    await reg.periodicSync.register(PULL_TAG, { minInterval: PULL_EVERY_MS });
  }
};

const requestPush = async () =>
  (await registration())?.sync?.register(PUSH_TAG);

// Sync goes on once the app is closed, where the browser allows it (Chrome
// on Android, ../sw.ts): changes left unpushed go out as soon as the device
// is online, and notes changed elsewhere are fetched ahead every so often.
export const useBackgroundSync = (leave: Leave) => {
  useEffect(() => {
    registerPull().catch(e => console.error('no periodic sync', e));
    const onHidden = () => {
      if (document.visibilityState !== 'hidden') return;
      leave()
        .then(staged => staged > 0 ? requestPush() : undefined)
        .catch(e => console.error('handing sync over failed', e));
    };
    document.addEventListener('visibilitychange', onHidden);
    return () => document.removeEventListener('visibilitychange', onHidden);
  }, [leave]);
};
